/**
 * Selectorul de perioadă al paginii de rapoarte financiare.
 *
 * Patru moduri — lună, trimestru, an, interval liber — plus baza de comparație
 * și pașii `‹ ›`. Patternul e cel din `components/reports/ReportFilterBar.tsx`:
 * un Select pentru mod, controale contextuale pentru fiecare mod, iar pentru
 * intervalul liber două `DateField`, care lucrează pe string-uri `YYYY-MM-DD`.
 *
 * Lunile deja închise sunt marcate în selectorul de lună, din lista de
 * închideri pe care backendul o expune de la început.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Select } from '../../ui/Select';
import { DateField } from '../../ui/DateField';
import { useI18n } from '../../../contexts/I18nContext';
import { monthNames } from '../../../lib/monthlyReportFormat';
import {
  quarterNames,
  shiftPeriod,
  switchKind,
  type ReportCompareMode,
  type ReportPeriodKind,
  type ReportPeriodState,
} from '../../../lib/reportPeriod';
import type { ClosingListItem } from '../../../lib/monthlyReportApi';

const KINDS: ReportPeriodKind[] = ['month', 'quarter', 'year', 'custom'];
const YEARS_BACK = 6;

export function ReportPeriodBar({
  value,
  onChange,
  closings = [],
  invalidRange = false,
}: {
  value: ReportPeriodState;
  onChange: (next: ReportPeriodState) => void;
  closings?: ClosingListItem[];
  /** True while the free interval is inverted; the message sits under the fields. */
  invalidRange?: boolean;
}) {
  const { t, locale } = useI18n();
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: YEARS_BACK }, (_, i) => currentYear - i);
  const months = monthNames(locale);
  const quarters = quarterNames(locale);
  const closedMonths = new Set(closings.map((c) => `${c.year}-${c.month}`));

  // Month / quarter: the year sits beside "Perioadă" and the arrows flank the
  // month. Year: the arrows flank the year itself.
  const hasSubPeriod = value.kind === 'month' || value.kind === 'quarter';
  const yearSelect = (className: string) => (
    <Select
      label={t('monthlyReport.yearLabel')}
      value={String(value.year)}
      onChange={(v) => onChange({ ...value, year: Number(v) })}
      options={years.map((y) => ({ value: String(y), label: String(y) }))}
      className={className}
    />
  );

  return (
    <div className="mt-6">
      {/* Phones: "Perioadă | An", then the arrows flanking what they step
          ("‹ Septembrie ›"), then the comparison, each row full width. From
          sm the wrapper dissolves (sm:contents) into one wrapping row. */}
      <div className="grid grid-cols-2 items-end gap-2 sm:flex sm:flex-wrap">
        <Select
          label={t('monthlyReport.period.kindLabel')}
          value={value.kind}
          onChange={(v) => onChange(switchKind(value, v as ReportPeriodKind))}
          options={KINDS.map((k) => ({
            value: k,
            label: t(`monthlyReport.period.kind.${k}` as 'monthlyReport.period.kind.month'),
          }))}
          className={`w-full sm:w-36 ${hasSubPeriod ? '' : 'col-span-2'}`}
        />

        {hasSubPeriod && yearSelect('w-full sm:w-28')}

        <div className="col-span-2 flex items-end gap-2 sm:contents">
          <button
            type="button"
            onClick={() => onChange(shiftPeriod(value, -1))}
            aria-label={t('monthlyReport.period.prev')}
            className="shrink-0 rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-50"
          >
            <ChevronLeft className="h-4 w-4" />
          </button>

          {value.kind === 'year' && yearSelect('min-w-0 flex-1 sm:w-28 sm:flex-none')}

          {value.kind === 'month' && (
            <Select
              label={t('monthlyReport.monthLabel')}
              value={String(value.month)}
              onChange={(v) => onChange({ ...value, month: Number(v) })}
              options={months.map((name, i) => ({
                value: String(i + 1),
                label: closedMonths.has(`${value.year}-${i + 1}`)
                  ? `${name} · ${t('monthlyReport.period.closedMonth')}`
                  : name,
              }))}
              className="min-w-0 flex-1 sm:w-48 sm:flex-none"
            />
          )}

          {value.kind === 'quarter' && (
            <Select
              label={t('monthlyReport.period.quarterLabel')}
              value={String(value.quarter)}
              onChange={(v) => onChange({ ...value, quarter: Number(v) })}
              options={quarters.map((name, i) => ({ value: String(i + 1), label: name }))}
              className="min-w-0 flex-1 sm:w-44 sm:flex-none"
            />
          )}

          {value.kind === 'custom' && (
            <>
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium text-gray-700 sm:flex-none">
                {t('monthlyReport.period.fromLabel')}
                <DateField
                  value={value.from}
                  onChange={(from) => onChange({ ...value, from })}
                  aria-invalid={invalidRange}
                  required
                  className="w-full sm:w-40"
                />
              </label>
              <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm font-medium text-gray-700 sm:flex-none">
                {t('monthlyReport.period.toLabel')}
                <DateField
                  value={value.to}
                  onChange={(to) => onChange({ ...value, to })}
                  aria-invalid={invalidRange}
                  required
                  className="w-full sm:w-40"
                />
              </label>
            </>
          )}

          <button
            type="button"
            onClick={() => onChange(shiftPeriod(value, 1))}
            aria-label={t('monthlyReport.period.next')}
            className="shrink-0 rounded border border-gray-300 p-2 text-gray-600 hover:bg-gray-50"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>

        <Select
          label={t('monthlyReport.period.compareLabel')}
          value={value.compare}
          onChange={(v) => onChange({ ...value, compare: v as ReportCompareMode })}
          options={(['prev', 'yoy'] as ReportCompareMode[]).map((m) => ({
            value: m,
            label: t(`monthlyReport.period.compare.${m}` as 'monthlyReport.period.compare.prev'),
          }))}
          className="col-span-2 w-full sm:w-56"
        />
      </div>
      {invalidRange && (
        <p className="mt-2 text-sm text-red-600">{t('monthlyReport.period.invalidRange')}</p>
      )}
    </div>
  );
}
